// dependencies
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// api
import {
	fetchHasTeamsAdminConsent,
	fetchTeamsAutoSyncSettings,
	fetchTeamsConfig,
	fetchTeamsConnectedUserCount,
	fetchTeamsLastSyncDate,
	fetchTeamsList,
	fetchTeamsMetadata,
	updateTeamsAutoSyncSettings,
} from "../../services";

export const useFetchTeamsConfig = () => {
	return useQuery({
		queryKey: ["teamsConfig"],
		queryFn: () => fetchTeamsConfig(),
		refetchOnWindowFocus: false,
		staleTime: 60 * 1000,
	});
};

export const useTeamsHasAdminConsent = () => {
	return useQuery({
		queryKey: ["teamsHasAdminConsent"],
		queryFn: () => fetchHasTeamsAdminConsent(),
		refetchOnWindowFocus: false,
		staleTime: 60 * 1000,
	});
};

export const useFetchTeamsMetadata = () => {
	return useQuery({
		queryKey: ["teamsMetadata"],
		queryFn: () => fetchTeamsMetadata(),
		refetchOnWindowFocus: false,
		staleTime: 60 * 1000,
	});
};

export const useFetchTeamsList = () => {
	return useQuery({
		queryKey: ["teamsList"],
		queryFn: () => fetchTeamsList(),
		refetchOnWindowFocus: false,
		staleTime: 60 * 1000,
	});
};

export const useTeamsAutoSyncSettings = () => {
	return useQuery({
		queryKey: ["getAutoSyncSettings"],
		queryFn: () => fetchTeamsAutoSyncSettings(),
		refetchOnWindowFocus: false,
	});
};

export const useTeamsConnectedUserCount = () => {
	return useQuery({
		queryKey: ["getConnectedUserCount"],
		queryFn: () => fetchTeamsConnectedUserCount(),
		refetchOnWindowFocus: false,
	});
};

export const useTeamsLastSyncDate = () => {
	return useQuery({
		queryKey: ["getLastSyncDate"],
		queryFn: () => fetchTeamsLastSyncDate(),
		refetchOnWindowFocus: false,
	});
};

export const useUpdateTeamsAutoSyncSettings = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: updateTeamsAutoSyncSettings,
		onMutate: async (newValue) => {
			// Cancel any outgoing refetches
			// (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({ queryKey: ["getAutoSyncSettings"] });

			// Snapshot the previous value
			const previousValue = queryClient.getQueryData(["getAutoSyncSettings"]);

			// Optimistically update to the new value
			queryClient.setQueryData(["getAutoSyncSettings"], () => newValue);

			// Return a context object with the snapshotted value
			return { previousValue };
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["getAutoSyncSettings"] });
		},
	});
};
